import { GIFT_PASS_BUNDLE_MAX_PASSES, GIFT_PASS_BUNDLE_MIN_PASSES } from '@bonsai/shared';
import { Minus, Plus } from 'lucide-react';

import { EditorFieldError } from '../../shared/editor-page/EditorFieldError';
import { EditorFormSection } from '../../shared/editor-page/EditorFormSection';

import { passCountOf } from './bundle-form';

import './gift-pass-bundle-editor.css';

export interface BundlePassCountSectionProps {
  value: string;
  error?: string;
  onChange: (passCount: string) => void;
}

function step(value: string, by: number): string {
  const current = passCountOf(value);
  const next = Number.isNaN(current) ? GIFT_PASS_BUNDLE_MIN_PASSES : current + by;
  return String(Math.min(Math.max(next, GIFT_PASS_BUNDLE_MIN_PASSES), GIFT_PASS_BUNDLE_MAX_PASSES));
}

export function BundlePassCountSection({ value, error, onChange }: BundlePassCountSectionProps) {
  const count = passCountOf(value);
  return (
    <EditorFormSection
      title="Passes"
      titleId="bundle-passes-title"
      hintId="bundle-passes-hint"
      hint="Exactly how many gift passes the bundle sells. A single pass is always sold on its own."
    >
      <div className="gpb-ed-row">
        <div className={`gpb-ed-stepper${error ? ' gpb-ed-invalid' : ''}`}>
          <button
            type="button"
            aria-label="One pass fewer"
            disabled={count <= GIFT_PASS_BUNDLE_MIN_PASSES}
            onClick={() => onChange(step(value, -1))}
          >
            <Minus size={14} aria-hidden />
          </button>
          <input
            aria-labelledby="bundle-passes-title"
            aria-describedby={error ? 'bundle-passes-error' : 'bundle-passes-hint'}
            aria-invalid={Boolean(error)}
            inputMode="numeric"
            value={value}
            onChange={(e) => onChange(e.target.value.replace(/\D/g, ''))}
          />
          <button
            type="button"
            aria-label="One pass more"
            disabled={count >= GIFT_PASS_BUNDLE_MAX_PASSES}
            onClick={() => onChange(step(value, 1))}
          >
            <Plus size={14} aria-hidden />
          </button>
        </div>
        {count >= GIFT_PASS_BUNDLE_MIN_PASSES && (
          <span className="gpb-ed-aside">
            A customer buys exactly <strong>{count}</strong> passes in one order.
          </span>
        )}
      </div>
      {error && <EditorFieldError id="bundle-passes-error">{error}</EditorFieldError>}
    </EditorFormSection>
  );
}
